import moment from 'moment-timezone';
import type {
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';

import type { PatientChatPatientSummary } from '@mummssoftware/utils';

import {
  formatDateTime,
  formatDuration,
  gatewayDay,
} from '../../utils/formatters';
import defaults from '../../translations/en.json';
import {
  HISTORY_NUMERIC_CLASS,
  isoComparator,
  numberComparator,
} from './historyColumnDefs';
import { getTokenColumnDefs, TokenCells } from './tokenColumnDefs';

type ColumnDefsArgs = {
  smallWidget?: boolean;
  siteTimezone?: string | null;
  /** "See tokens" is on (Mumms admins): token split and cost columns. */
  showCost?: boolean;
};

const textFilter: ColDef = {
  filter: 'agTextColumnFilter',
  menuTabs: ['filterMenuTab'],
  filterParams: {
    buttons: ['reset'],
    filterOptions: ['contains'],
    suppressAndOrCondition: true,
  },
};

const numberFilter: ColDef = {
  filter: 'agNumberColumnFilter',
  menuTabs: ['filterMenuTab'],
  filterParams: {
    buttons: ['apply', 'reset'],
    closeOnApply: true,
    defaultOption: 'inRange',
    filterOptions: ['inRange', 'greaterThanOrEqual', 'lessThanOrEqual'],
    inRangeInclusive: true,
    suppressAndOrCondition: true,
  },
};

/**
 * AG Grid's date filter: <0 when the cell's day is before the picked day, >0
 * after, 0 the same. The cell's day is taken in the site's zone, the day shown.
 */
export const dayComparator = (siteTimezone?: string | null) => (
  filterDate: Date,
  cellValue: string | null,
): number => {
  const day = gatewayDay(cellValue, siteTimezone);
  return day ? day.localeCompare(moment(filterDate).format('YYYY-MM-DD')) : -1;
};

const dateFilter = (siteTimezone?: string | null): ColDef => ({
  filter: 'agDateColumnFilter',
  menuTabs: ['filterMenuTab'],
  filterParams: {
    comparator: dayComparator(siteTimezone),
    buttons: ['apply', 'reset'],
    closeOnApply: true,
    defaultOption: 'inRange',
    filterOptions: ['inRange', 'equals', 'greaterThan', 'lessThan'],
    inRangeInclusive: true,
    suppressAndOrCondition: true,
  },
});

const rowOf = (data: unknown) => data as PatientChatPatientSummary | undefined;

const patientCells = (data: unknown): TokenCells | undefined => {
  const row = rowOf(data);
  return row
    ? {
        tokens: row.totalTokensUsed,
        input: row.totalInputTokens,
        cached: row.totalCachedTokens,
        output: row.totalOutputTokens,
        candidates: row.totalCandidatesTokens,
        thoughts: row.totalThoughtsTokens,
        questionsCost: row.questionsCostUsd,
        cacheCost: row.cacheCostUsd,
        totalCost: row.totalEstimatedCostUsd,
      }
    : undefined;
};

const dateTimeFormatter = (siteTimezone?: string | null) => ({
  value,
}: ValueFormatterParams) => formatDateTime(value, siteTimezone);

/**
 * MAIA Chats: one row per patient. Patient Name · MRN · First question ·
 * Last question (newest first) · Conversations · Messages (answered
 * questions) · Duration (sum of the answers' times) · Tokens, and with "See
 * tokens" on, Tokens In · Tokens Out · Questions $ · Chart cache $ · Total $.
 * Every column sorts and filters: text for name and number, ranges for the
 * counts (Duration in seconds) and dates. The tile drops First and Last
 * question; it still sorts on Last question.
 */
export const getPatientListColumnDefs = ({
  smallWidget,
  siteTimezone,
  showCost,
}: ColumnDefsArgs): ColDef[] => [
  {
    colId: 'patientName',
    field: 'patientName',
    headerName: defaults.patientsName,
    flex: 1,
    minWidth: 100,
    valueGetter: ({ data }: ValueGetterParams) =>
      rowOf(data)?.patientName || '',
    tooltipField: 'patientName',
    ...textFilter,
  },
  {
    colId: 'patientNumber',
    field: 'patientNumber',
    headerName: defaults.patientsNumber,
    width: smallWidget ? 80 : 100,
    ...textFilter,
  },
  {
    colId: 'firstQuestionAt',
    field: 'firstQuestionAt',
    headerName: defaults.patientsFirstQuestion,
    width: 140,
    hide: smallWidget,
    cellClass: HISTORY_NUMERIC_CLASS,
    comparator: isoComparator,
    valueFormatter: dateTimeFormatter(siteTimezone),
    ...dateFilter(siteTimezone),
  },
  {
    colId: 'lastQuestionAt',
    field: 'lastQuestionAt',
    headerName: defaults.patientsLastQuestion,
    width: 140,
    hide: smallWidget,
    sort: 'desc',
    cellClass: HISTORY_NUMERIC_CLASS,
    comparator: isoComparator,
    valueFormatter: dateTimeFormatter(siteTimezone),
    ...dateFilter(siteTimezone),
  },
  {
    colId: 'conversationCount',
    field: 'conversationCount',
    headerName: defaults.patientsConversations,
    width: smallWidget ? 70 : 120,
    cellClass: HISTORY_NUMERIC_CLASS,
    comparator: numberComparator,
    ...numberFilter,
  },
  {
    colId: 'questionCount',
    field: 'questionCount',
    headerName: defaults.patientsMessages,
    width: smallWidget ? 70 : 100,
    cellClass: HISTORY_NUMERIC_CLASS,
    comparator: numberComparator,
    ...numberFilter,
  },
  {
    colId: 'duration',
    headerName: defaults.historyDuration,
    width: smallWidget ? 64 : 90,
    cellClass: HISTORY_NUMERIC_CLASS,
    comparator: numberComparator,
    // seconds, so the range filter is typed in seconds
    field: 'totalDurationSeconds',
    valueFormatter: ({ value }: ValueFormatterParams) => formatDuration(value),
    ...numberFilter,
  },
  ...getTokenColumnDefs({
    cells: patientCells,
    showCost,
    smallWidget,
    filter: numberFilter,
  }),
];
